import * as nodePath from 'node:path';
import { computed, map } from 'nanostores';
import type { ChangeQueue } from './change-queue';
import { checkMissingSecrets } from './find-secrets';
import type { SupabaseStore } from './index';
import { logger } from './utils';
import { deployEdgeFunction, getEdgeFunctionLogs, listEdgeFunctions } from '~/lib/.client/api/supabase';
import {
  STRIPE_WEBHOOK_SLUG,
  SUPABASE_EDGE_FUNCTIONS_FOLDER,
  SUPABASE_EDGE_FUNCTIONS_SHARED_FOLDER,
} from '~/lib/constants';
import { errorHandler } from '~/lib/error-handling';
import { waitForProjectImport } from '~/lib/projects';
import type { FileMap } from '~/lib/stores/files';
import { walkFileMap } from '~/lib/stores/files/filemap';
import { projectStore } from '~/lib/stores/project';
import { workbenchStore } from '~/lib/stores/workbench';
import { hmrPropertyFactory } from '~/utils/hmr';
import { waitForFirstValue, waitUntil } from '~/utils/nanostore';
import { NOOP } from '~/utils/noops';
import { promiseQueue, toSyncCallback, type PromiseWithQueue } from '~/utils/promises';
import { addWorkDir, removeWorkDir } from '~/utils/workDir';

interface DeployedEdgeFunction {
  contentHash: string | undefined;
  verifyJWT: boolean;
  id: string | undefined;
  status: 'DEPLOYED' | 'FAILED';
}

const CANCEL_REASON = Symbol();

const hmr = hmrPropertyFactory('SupabaseEdgeFunctionsStore');

/**
 * Global store holding all the state related to Supabase.
 */
export class SupabaseEdgeFunctionsStore {
  @hmr()
  #functionsBeingDeployed = map<Record<string, boolean>>({});

  @hmr()
  #trackDeployedFunctions: Map<string, DeployedEdgeFunction> = new Map();

  @hmr()
  #abortController = new AbortController();

  #functionsUpdate: PromiseWithQueue<void> | undefined;

  #previousUpdate = Promise.resolve();

  #functionsLogDebounce = new Map<string, ReturnType<typeof setTimeout>>();

  /**
   * If this is true it means that there's at least one function that is being deployed.
   */
  isDeploying = computed(this.#functionsBeingDeployed, (functions) =>
    Object.keys(functions).some((functionSlug) => functions[functionSlug]),
  );

  constructor(
    private _supabaseStore: SupabaseStore,
    private _changesQueue: ChangeQueue,
  ) {}

  init() {
    this.#trackSupabaseEdgeFunctions();
  }

  async deployEdgeFunctions(): Promise<void> {
    return this.#functionsUpdate?.promise.then(NOOP);
  }

  async getEdgeFunctionsLogs(functionSlug: string) {
    const projectId = projectStore.getProjectId();
    const functionId = this.#trackDeployedFunctions.get(functionSlug)?.id;

    if (!functionId || !projectId) {
      return [];
    }

    return new Promise<Awaited<ReturnType<typeof getEdgeFunctionLogs>>>((resolve) => {
      clearTimeout(this.#functionsLogDebounce.get(functionSlug));

      this.#functionsLogDebounce.set(
        functionSlug,
        setTimeout(() => {
          resolve(getEdgeFunctionLogs(projectId, functionId));
        }, 1000),
      );
    });
  }

  async #trackSupabaseEdgeFunctions(): Promise<any> {
    await waitForProjectImport();

    const projectId = await waitForFirstValue(projectStore.projectId);

    const unsubscribeFromEdgeFunctionUpdate = workbenchStore.edgeFunctionsFiles.subscribe((functions, prev) => {
      if (!prev) {
        return;
      }

      if (Object.keys(functions).length > 0 && this._supabaseStore.supabaseStatus.get() === 'NOT_CONFIGURED') {
        this._changesQueue.injectConnectRequired();
      }
    });

    // make sure that the project is configured first with supabase otherwise do nothing
    await waitUntil(this._supabaseStore.isConfigured, true);

    unsubscribeFromEdgeFunctionUpdate();

    const ERR_SYMBOL = Symbol('ERR_SYMBOL');

    const [deployedFunctions, edgeFunctionsFiles] = await Promise.all([
      listEdgeFunctions(projectId).catch<typeof ERR_SYMBOL>(() => ERR_SYMBOL),
      waitForFirstValue(workbenchStore.edgeFunctionsFiles),
    ]);

    let cleanup: VoidFunction | undefined;

    if (deployedFunctions === ERR_SYMBOL) {
      const unsubscribe = workbenchStore.edgeFunctionsFiles.subscribe((_, prev) => {
        if (prev) {
          cleanup = toSyncCallback(this.#trackSupabaseEdgeFunctions());

          unsubscribe();
        }
      });

      return () => {
        unsubscribe();

        cleanup?.();
      };
    }

    // we need to track the deployed functions so we can deploy them if they change
    await Promise.all(
      deployedFunctions.map(async ({ slug, id, verifyJWT }) => {
        const contentHash = await workbenchStore.computeContentHash(getFullPathFromSlug(slug), {
          files: edgeFunctionsFiles,
        });

        this.#trackDeployedFunctions.set(slug, { contentHash, id, verifyJWT, status: 'DEPLOYED' });
      }),
    );

    logger.debug('Edge functions', deployedFunctions);

    // when the edge function files changes we check them
    return workbenchStore.edgeFunctionsFiles.subscribe((edgeFunctionsFiles) => {
      checkMissingSecrets(edgeFunctionsFiles);

      this.#triggerCheck(edgeFunctionsFiles);
    });
  }

  async #fetchDeployedFunctionSettings() {
    try {
      const projectId = projectStore.projectId.get()!;
      const deployedFunctions = await listEdgeFunctions(projectId);

      for (const { slug, verifyJWT } of deployedFunctions) {
        const functionInfo = this.#trackDeployedFunctions.get(slug);

        if (functionInfo) {
          functionInfo.verifyJWT = verifyJWT;
        }
      }
    } catch (error) {
      logger.error(error);
    }
  }

  #triggerCheck(edgeFunctionsFiles: FileMap) {
    /**
     * If there are no checks pending we create a new promise queue which
     * will resolve or reject when all the promises that it tracks have
     * completed or rejected.
     */
    if (!this.#functionsUpdate) {
      this.#functionsUpdate = promiseQueue<void>();

      this.#functionsUpdate.promise.finally(() => {
        this.#functionsUpdate = undefined;
      });
    }

    const previousUpdate = this.#previousUpdate;

    this.#abortController.abort(CANCEL_REASON);
    this.#abortController = new AbortController();

    this.#previousUpdate = this.#checkIfThereWereAnyChanges(
      edgeFunctionsFiles,
      previousUpdate,
      this.#abortController.signal,
    ).catch((reason) => {
      /**
       * If we've aborted, it means that we've started another check.
       * In that case, we don't call resolve on `#functionsUpdate` as
       * the update hasn't completed yet.
       */
      if (reason !== CANCEL_REASON) {
        throw reason;
      }
    });

    this.#functionsUpdate.enqueue(this.#previousUpdate);
  }

  async #checkIfThereWereAnyChanges(
    edgeFunctionsFiles: FileMap,
    previousUpdate: Promise<void> | undefined,
    signal: AbortSignal,
  ) {
    /**
     * The previous update was cancelled but it might have reached the phase where
     * it is no longer cancellable in which case we should wait for it to have
     * completed.
     */
    await previousUpdate;

    signal.throwIfAborted();

    // wait for streaming and all the fixing to be complete
    await waitUntil(workbenchStore.isProcessing, false);

    signal.throwIfAborted();

    /**
     * The `functions` folder might look something like this:
     *
     *  - /supabase/functions/foo
     *      - index.ts
     *      - import_map.json
     *      - utils.ts
     *  - /supabase/functions/bar
     *      - index.ts
     *
     * So we want to group them by folder and compute the content hash
     * of each folder to see if there has been any changes. If the content
     * hash has changed we trigger a new deployment.
     */
    const edgeFunctions = new Set<string>();

    for (const filePath in edgeFunctionsFiles) {
      const file = edgeFunctionsFiles[filePath];

      if (file?.type !== 'file') {
        continue;
      }

      const functionSlug = getSlugFromFullPath(filePath);

      if (!functionSlug) {
        logger.warn(`Could not resolve an edge function slug from ${filePath}`);
        continue;
      }

      edgeFunctions.add(functionSlug);
    }

    const functionToDeploy = new Map<string, string>();

    // compute the content hash
    for (const functionSlug of edgeFunctions) {
      // FIXME: replace contentHash with string concatenation
      const contentHash = await workbenchStore.computeContentHash(getFullPathFromSlug(functionSlug), {
        signal,
        files: edgeFunctionsFiles,
      });

      const trackedInfo = this.#trackDeployedFunctions.get(functionSlug);

      if (trackedInfo?.contentHash !== contentHash) {
        functionToDeploy.set(functionSlug, contentHash);
      }
    }

    signal.throwIfAborted();

    // if there's nothing to do, we stop there
    if (functionToDeploy.size === 0) {
      return;
    }

    /**
     * At this point the update is no longer cancellable. So to make sure
     * we dedupe the events, we will persists the new content hashes.
     */
    for (const [functionSlug, contentHash] of functionToDeploy) {
      const trackedInfo = this.#trackDeployedFunctions.get(functionSlug);

      if (trackedInfo) {
        trackedInfo.contentHash = contentHash;
      }
    }

    /**
     * Right before updating all the functions we get their settings to
     * make sure we don't revert any options the user would have had configured
     * manually in the supabase dashboard.
     */
    await this.#fetchDeployedFunctionSettings();

    // deploy functions one at a time
    for (const [functionSlug, contentHash] of functionToDeploy) {
      await this.#deployFunction(functionSlug, contentHash, edgeFunctionsFiles);
    }
  }

  async #deployFunction(slug: string, contentHash: string, edgeFunctionsFiles: FileMap) {
    const functionFolderPath = getFullPathFromSlug(slug);

    const sharedFolderPath = getFullPathFromSlug(SUPABASE_EDGE_FUNCTIONS_SHARED_FOLDER);

    const functionFiles = [
      ...walkFileMap(edgeFunctionsFiles, functionFolderPath),
      ...walkFileMap(edgeFunctionsFiles, sharedFolderPath),
    ]
      .map(({ dirent, path }) => ({ ...dirent, path })) // this map is only needed to have typescript see the filter as a type predicate
      .filter((dirent) => dirent.type === 'file')
      .map((dirent) => [nodePath.relative(functionFolderPath, dirent.path), dirent.content]);

    this.#functionsBeingDeployed.setKey(slug, true);

    /**
     * We preserve the flag we got from Supabase after a deployment. By default we always verify
     * the jwt.
     */
    const { verifyJWT = shouldVerifyJWT(slug) } = this.#trackDeployedFunctions.get(slug) ?? {};

    try {
      const files: Record<string, string> = Object.fromEntries(functionFiles);

      const importMap = 'deno.json' in files ? 'deno.json' : 'import_map.json' in files ? 'import_map.json' : undefined;

      const id = await deployEdgeFunction(projectStore.getProjectId()!, {
        name: slug,
        slug,
        entrypoint: 'index.ts',
        files,
        verifyJWT,

        ...(importMap ? { importMap } : {}),
      });

      logger.info('deployed function', slug, id);

      this.#trackDeployedFunctions.set(slug, { contentHash, id, verifyJWT, status: 'DEPLOYED' });
    } catch (error) {
      logger.error(`Failed to deploy '${slug}'`, error);

      const errorMessage = error instanceof Error ? error.message : `${error}`;

      errorHandler.push({
        type: 'supabase-function',
        functionSlug: slug,
        message: `Failed to deploy edge function: ${errorMessage}`,
      });

      const previousId = this.#trackDeployedFunctions.get(slug)?.id;

      this.#trackDeployedFunctions.set(slug, { contentHash, id: previousId, verifyJWT, status: 'FAILED' });
    } finally {
      this.#functionsBeingDeployed.setKey(slug, false);
    }
  }
}

function getFullPathFromSlug(slug: string) {
  return addWorkDir(`${SUPABASE_EDGE_FUNCTIONS_FOLDER}/${slug}`);
}

/**
 * Get the function slug a file is included in. That is it returns the following part:
 *
 * `${SUPABASE_EDGE_FUNCTIONS_FOLDER}/${slug}`
 *
 * This works for any file present in the folder.
 *
 * @param filePath Provided path expected to be under the SUPABASE_EDGE_FUNCTIONS_FOLDER folder.
 */
function getSlugFromFullPath(filePath: string): string | undefined {
  const projectPath = removeWorkDir(filePath);

  if (!projectPath.startsWith(SUPABASE_EDGE_FUNCTIONS_FOLDER + '/')) {
    return undefined;
  }

  const nextSlash = projectPath.indexOf('/', SUPABASE_EDGE_FUNCTIONS_FOLDER.length + 1);

  let slug;

  if (nextSlash !== -1) {
    slug = projectPath.slice(SUPABASE_EDGE_FUNCTIONS_FOLDER.length + 1, nextSlash);
  } else {
    slug = projectPath.slice(SUPABASE_EDGE_FUNCTIONS_FOLDER.length + 1);
  }

  // folders that starts with an underscore should not be deployed
  if (!slug.startsWith('_')) {
    return slug;
  }

  return undefined;
}

/**
 * We use the convention that edge functions that have webhook in their
 * name won't want to verify the JWT because they are actually webhooks.
 */
function shouldVerifyJWT(slug: string) {
  return !slug.includes('webhook') && slug !== STRIPE_WEBHOOK_SLUG;
}
